import { useState, useEffect, useMemo } from 'react';
import {
  AdminBillingData,
  BillingSubscription,
  PaymentIssue,
  BillingMetrics,
  PaymentIssueMetrics,
  RevenueMetrics,
  MRRBreakdown,
  RevenueTrendDataPoint,
  RevenueByTierDataPoint,
  ChurnAnalysis,
  PaymentIssueDistribution,
  PaddleWebhookEvent,
  WebhookEventDistribution,
} from '../../types/admin';
import { subDays, subMonths, format, addHours } from 'date-fns';

/**
 * useAdminBilling - Hook for billing and subscription management
 *
 * Generates realistic mock data for:
 * - Subscriptions (3,247 active)
 * - Payment issues (63 recent issues)
 * - Revenue metrics (MRR, ARR, ARPU, LTV, NRR)
 * - Revenue trends and forecasts
 * - Churn analysis by tier
 * - Paddle integration status
 * - Webhook events
 */

// Helper functions
function randomWithVariance(base: number, variance: number): number {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
}

// Generate subscriptions
function generateSubscriptions(count: number = 3247): BillingSubscription[] {
  const subscriptions: BillingSubscription[] = [];
  const tiers = [
    { tier: 'pro' as const, amount: 19, weight: 75 },
    { tier: 'agent' as const, amount: 49, weight: 21 },
    { tier: 'business' as const, amount: 199, weight: 4 },
  ];

  for (let i = 0; i < count; i++) {
    const random = Math.random() * 100;
    let tier = tiers[0];
    let cumWeight = 0;
    for (const t of tiers) {
      cumWeight += t.weight;
      if (random < cumWeight) {
        tier = t;
        break;
      }
    }

    const statusRandom = Math.random() * 100;
    let status: BillingSubscription['status'] = 'active';
    if (statusRandom < 3) status = 'past_due';
    else if (statusRandom < 7) status = 'trial';
    else if (statusRandom < 8) status = 'canceled';

    const createdAt = subDays(new Date(), randomWithVariance(180, 150));
    const currentPeriodStart = subDays(new Date(), randomWithVariance(15, 10));

    subscriptions.push({
      id: `sub_${i + 1}`,
      user_id: `user_${i + 1}`,
      user_email: `user${i + 1}@example.com`,
      user_name: `User ${i + 1}`,
      paddle_subscription_id: `paddle_sub_${Math.random().toString(36).substr(2, 9)}`,
      paddle_customer_id: `paddle_cust_${Math.random().toString(36).substr(2, 9)}`,
      tier: tier.tier,
      status,
      amount: tier.amount,
      currency: 'USD',
      interval: Math.random() > 0.9 ? 'year' : 'month',
      payment_method: Math.random() > 0.15 ? 'card' : 'paypal',
      payment_method_last4: Math.floor(Math.random() * 10000).toString().padStart(4, '0'),
      payment_method_brand: Math.random() > 0.4 ? 'visa' : Math.random() > 0.5 ? 'mastercard' : 'amex',
      current_period_start: format(currentPeriodStart, 'yyyy-MM-dd'),
      current_period_end: format(addHours(currentPeriodStart, 720), 'yyyy-MM-dd'),
      trial_end: status === 'trial' ? format(addHours(new Date(), 168), 'yyyy-MM-dd') : undefined,
      created_at: format(createdAt, 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), randomWithVariance(5, 4)), 'yyyy-MM-dd HH:mm:ss'),
    });
  }

  return subscriptions;
}

// Generate payment issues
function generatePaymentIssues(count: number = 63): PaymentIssue[] {
  const issues: PaymentIssue[] = [];
  const types = [
    { type: 'failed_payment' as const, weight: 65 },
    { type: 'expired_card' as const, weight: 25 },
    { type: 'declined' as const, weight: 8 },
    { type: 'chargeback' as const, weight: 2 },
  ];

  for (let i = 0; i < count; i++) {
    const random = Math.random() * 100;
    let type = types[0];
    let cumWeight = 0;
    for (const t of types) {
      cumWeight += t.weight;
      if (random < cumWeight) {
        type = t;
        break;
      }
    }

    const statusRandom = Math.random() * 100;
    let status: PaymentIssue['status'] = 'new';
    if (statusRandom < 30) status = 'retrying';
    else if (statusRandom < 50) status = 'resolved';
    else if (statusRandom < 55) status = 'given_up';

    const issueDate = subDays(new Date(), randomWithVariance(7, 6));
    const amount = [19, 49, 199][Math.floor(Math.random() * 3)];

    issues.push({
      id: `issue_${i + 1}`,
      user_id: `user_${randomWithVariance(500, 450)}`,
      user_email: `user${randomWithVariance(500, 450)}@example.com`,
      user_name: `User ${randomWithVariance(500, 450)}`,
      subscription_id: `sub_${randomWithVariance(500, 450)}`,
      paddle_subscription_id: `paddle_sub_${Math.random().toString(36).substr(2, 9)}`,
      issue_type: type.type,
      amount,
      currency: 'USD',
      issue_date: format(issueDate, 'yyyy-MM-dd HH:mm:ss'),
      status,
      retry_count: status === 'retrying' ? randomWithVariance(2, 1) : 0,
      max_retries: 3,
      last_retry_at: status === 'retrying' ? format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss') : undefined,
      next_retry_at: status === 'retrying' ? format(addHours(new Date(), 6), 'yyyy-MM-dd HH:mm:ss') : undefined,
      error_message: type.type === 'failed_payment' ? 'Insufficient funds' : 'Card expired',
      resolved_at: status === 'resolved' ? format(subDays(new Date(), randomWithVariance(2, 2)), 'yyyy-MM-dd HH:mm:ss') : undefined,
      created_at: format(issueDate, 'yyyy-MM-dd HH:mm:ss'),
      updated_at: format(subDays(new Date(), randomWithVariance(1, 1)), 'yyyy-MM-dd HH:mm:ss'),
    });
  }

  return issues.sort((a, b) => b.issue_date.localeCompare(a.issue_date));
}

// Calculate metrics
function calculateBillingMetrics(subscriptions: BillingSubscription[]): BillingMetrics {
  const active = subscriptions.filter(s => s.status === 'active').length;
  const trial = subscriptions.filter(s => s.status === 'trial').length;
  const pastDue = subscriptions.filter(s => s.status === 'past_due').length;

  return {
    active_subscriptions: active,
    active_subscriptions_change: 5.2,
    trial_subscriptions: trial,
    past_due_subscriptions: pastDue,
    canceled_this_month: randomWithVariance(58, 10),
    new_mrr: randomWithVariance(4230, 500),
    new_mrr_change: 12.3,
    churned_mrr: randomWithVariance(1450, 200),
    churned_mrr_change: -3.1,
    churn_rate: 2.3,
    churn_rate_change: -0.5,
  };
}

function calculatePaymentIssueMetrics(issues: PaymentIssue[]): PaymentIssueMetrics {
  const failedPayments = issues.filter(i => i.issue_type === 'failed_payment').length;
  const expiredCards = issues.filter(i => i.issue_type === 'expired_card').length;
  const atRiskRevenue = issues.reduce((sum, i) => sum + i.amount, 0);

  return {
    failed_payments: failedPayments,
    failed_payments_change: -8,
    expired_cards: expiredCards,
    expired_cards_change: 2,
    disputes: issues.filter(i => i.issue_type === 'dispute').length,
    chargebacks: issues.filter(i => i.issue_type === 'chargeback').length,
    at_risk_revenue: atRiskRevenue,
    resolved_this_week: issues.filter(i => i.status === 'resolved').length,
    auto_recovery_rate: 65,
  };
}

function calculateRevenueMetrics(subscriptions: BillingSubscription[]): RevenueMetrics {
  const mrr = subscriptions
    .filter(s => s.status === 'active' || s.status === 'trial')
    .reduce((sum, s) => sum + (s.interval === 'year' ? s.amount / 12 : s.amount), 0);

  return {
    mrr: Math.round(mrr),
    mrr_change: 8.5,
    arr: Math.round(mrr * 12),
    arr_change: 8.5,
    arpu: parseFloat((mrr / subscriptions.filter(s => s.status === 'active').length).toFixed(2)),
    arpu_change: 2.1,
    ltv: 1850,
    ltv_change: 5.3,
    nrr: 105,
    nrr_change: 2.0,
  };
}

// Generate revenue trend
function generateRevenueTrend(months: number = 12): RevenueTrendDataPoint[] {
  const data: RevenueTrendDataPoint[] = [];
  let currentMRR = 35000;

  for (let i = months - 1; i >= -3; i--) {
    const date = format(subMonths(new Date(), i), 'yyyy-MM');
    const isForecast = i < 0;

    if (!isForecast) {
      currentMRR = Math.round(currentMRR * 1.07); // 7% monthly growth
    } else {
      currentMRR = Math.round(currentMRR * 1.065); // Forecast slower
    }

    data.push({
      date,
      mrr: currentMRR,
      arr: currentMRR * 12,
      new_mrr: randomWithVariance(3500, 500),
      churned_mrr: randomWithVariance(1200, 300),
      is_forecast: isForecast,
    });
  }

  return data;
}

// Generate revenue by tier
function generateRevenueByTier(months: number = 12): RevenueByTierDataPoint[] {
  const data: RevenueByTierDataPoint[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const date = format(subMonths(new Date(), i), 'yyyy-MM');
    const growth = 1 + (0.07 * (months - i)) / months;

    data.push({
      date,
      pro_revenue: Math.round(15000 * growth),
      agent_revenue: Math.round(25000 * growth),
      business_revenue: Math.round(7000 * growth),
    });
  }

  return data;
}

// Generate churn analysis
function generateChurnAnalysis(): ChurnAnalysis[] {
  return [
    {
      id: 'churn_pro',
      tier: 'pro',
      active_subscriptions: 2450,
      churned_this_month: 58,
      churn_rate: 2.4,
      voluntary_churn: 45,
      involuntary_churn: 13,
    },
    {
      id: 'churn_agent',
      tier: 'agent',
      active_subscriptions: 680,
      churned_this_month: 12,
      churn_rate: 1.8,
      voluntary_churn: 8,
      involuntary_churn: 4,
    },
    {
      id: 'churn_business',
      tier: 'business',
      active_subscriptions: 117,
      churned_this_month: 2,
      churn_rate: 1.7,
      voluntary_churn: 1,
      involuntary_churn: 1,
    },
  ];
}

// Generate webhook events
function generateWebhookEvents(count: number = 50): PaddleWebhookEvent[] {
  const events: PaddleWebhookEvent[] = [];
  const eventTypes = [
    'subscription.created',
    'subscription.updated',
    'subscription.canceled',
    'payment.succeeded',
    'payment.failed',
  ];

  for (let i = 0; i < count; i++) {
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const status = Math.random() > 0.05 ? 'success' : 'failed';

    events.push({
      id: `webhook_${i + 1}`,
      event_type: eventType,
      paddle_subscription_id: `paddle_sub_${Math.random().toString(36).substr(2, 9)}`,
      received_at: format(subDays(new Date(), randomWithVariance(i / 10, i / 20)), 'yyyy-MM-dd HH:mm:ss'),
      processing_status: status as 'success' | 'failed',
      retry_count: status === 'failed' ? randomWithVariance(1, 1) : 0,
      error_message: status === 'failed' ? 'Database connection timeout' : undefined,
      payload: {},
    });
  }

  return events.sort((a, b) => b.received_at.localeCompare(a.received_at));
}

export function useAdminBilling() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const billingData = useMemo<AdminBillingData>(() => {
    const subscriptions = generateSubscriptions(3247);
    const paymentIssues = generatePaymentIssues(63);
    const revenueMetrics = calculateRevenueMetrics(subscriptions);

    const mrrBreakdown: MRRBreakdown = {
      starting_mrr: 45000,
      new_mrr: 4230,
      expansion_mrr: 1200,
      contraction_mrr: 800,
      churned_mrr: 1670,
      ending_mrr: revenueMetrics.mrr,
    };

    const paymentIssueDistribution: PaymentIssueDistribution[] = [
      { issue_type: 'failed_payment', count: 41, percentage: 65, at_risk_amount: 1890 },
      { issue_type: 'expired_card', count: 16, percentage: 25, at_risk_amount: 450 },
      { issue_type: 'declined', count: 5, percentage: 8, at_risk_amount: 180 },
      { issue_type: 'chargeback', count: 1, percentage: 2, at_risk_amount: 49 },
    ];

    const webhookEventDistribution: WebhookEventDistribution[] = [
      { event_type: 'subscription.created', count: 245, success_rate: 99.2 },
      { event_type: 'subscription.updated', count: 180, success_rate: 98.9 },
      { event_type: 'payment.succeeded', count: 320, success_rate: 99.7 },
      { event_type: 'payment.failed', count: 42, success_rate: 97.6 },
      { event_type: 'subscription.canceled', count: 58, success_rate: 100 },
    ];

    return {
      metrics: calculateBillingMetrics(subscriptions),
      revenueMetrics,
      paymentIssueMetrics: calculatePaymentIssueMetrics(paymentIssues),
      subscriptions,
      paymentIssues,
      mrrBreakdown,
      revenueTrend: generateRevenueTrend(12),
      revenueByTier: generateRevenueByTier(12),
      churnAnalysis: generateChurnAnalysis(),
      paymentIssueDistribution,
      paddleStatus: {
        api_connected: true,
        api_last_checked: format(subDays(new Date(), 0), 'yyyy-MM-dd HH:mm:ss'),
        last_sync_at: format(subDays(new Date(), 0), 'yyyy-MM-dd HH:mm:ss'),
        webhook_success_rate: 98.5,
        failed_webhooks_today: 3,
        total_webhooks_today: 215,
      },
      recentWebhookEvents: generateWebhookEvents(50),
      webhookEventDistribution,
      lastUpdated: format(lastRefresh, 'yyyy-MM-dd HH:mm:ss'),
    };
  }, [lastRefresh]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [lastRefresh]);

  const refresh = () => setLastRefresh(new Date());

  const cancelSubscription = async (subscriptionId: string, reason?: string) => {
    console.log('Canceling subscription:', subscriptionId, reason);
    setTimeout(() => refresh(), 500);
    return { success: true };
  };

  const retryPayment = async (issueId: string) => {
    console.log('Retrying payment:', issueId);
    setTimeout(() => refresh(), 500);
    return { success: true };
  };

  return {
    billingData,
    isLoading,
    refresh,
    cancelSubscription,
    retryPayment,
  };
}
